import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { GridOn } from '@mui/icons-material';

const Conversiones = () => {
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);

    // Exportar a Excel
    const handleExportExcel = async () => {
      const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
      const search = searchValue.toLowerCase();
      const rows = conversiones
        .filter(conv =>
          conv.unidad_origen_nombre.toLowerCase().includes(search) ||
          conv.unidad_destino_nombre.toLowerCase().includes(search) ||
          (conv.unidad_origen_abreviatura || '').toLowerCase().includes(search) ||
          (conv.unidad_destino_abreviatura || '').toLowerCase().includes(search) ||
          conv.id.toString().includes(search) ||
          conv.factor.toString().includes(search)
        )
        .map(conv => ({
          id: conv.id,
          unidadOrigen: `${conv.unidad_origen_nombre} (${conv.unidad_origen_abreviatura})`,
          unidadDestino: `${conv.unidad_destino_nombre} (${conv.unidad_destino_abreviatura})`,
          factor: conv.factor
        }));
      await exportGenericWorkbook({
        titulo: 'REPORTE DE CONVERSIONES DE UNIDAD',
        sheetName: 'Conversiones',
        tableName: 'tblConversiones',
        fileName: 'Conversiones',
        columns: [
          { key: 'id', label: 'ID', width: 8 },
          { key: 'unidadOrigen', label: 'Unidad Origen', width: 28 },
          { key: 'unidadDestino', label: 'Unidad Destino', width: 28 },
          { key: 'factor', label: 'Factor de Conversión', width: 22 }
        ],
        rows,
        searchText: searchValue,
        usuario: ''
      });
    };
  const [conversiones, setConversiones] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [unidadOrigen, setUnidadOrigen] = useState('');
  const [unidadDestino, setUnidadDestino] = useState('');
  const [factor, setFactor] = useState('');

  const fetchConversiones = async () => {
    const res = await axios.get('/api/conversiones');
    setConversiones(res.data);
  };

  const fetchUnidades = async () => {
    const res = await axios.get('/api/unidades');
    setUnidades(res.data);
  };

  useEffect(() => {
    fetchConversiones();
    fetchUnidades();
  }, []);

  const handleOpen = (conv = null) => {
    if (conv) {
      setEditId(conv.id);
      setUnidadOrigen(conv.unidad_origen_id);
      setUnidadDestino(conv.unidad_destino_id);
      setFactor(conv.factor);
    } else {
      setEditId(null);
      setUnidadOrigen('');
      setUnidadDestino('');
      setFactor('');
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setUnidadOrigen('');
    setUnidadDestino('');
    setFactor('');
  };

  const handleSave = async () => {
    if (!unidadOrigen || !unidadDestino || !factor) return;
    const data = { unidad_origen_id: unidadOrigen, unidad_destino_id: unidadDestino, factor };
    try {
      if (editId) {
        await axios.put(`/api/conversiones/${editId}`, data);
      } else {
        await axios.post('/api/conversiones', data);
      }
      fetchConversiones();
      handleClose();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/conversiones/${id}`);
      fetchConversiones();
    } catch { alert('Error al eliminar'); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Conversiones de Unidades</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar..."
            value={searchValue}
            onChange={e => { setSearchValue(e.target.value); setPage(0); }}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button color="success" variant="contained" sx={{ minWidth: 120 }} onClick={handleExportExcel} startIcon={<GridOn />}>
            Exportar Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>Nueva Conversión</Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ pr: 8 }}>ID</TableCell>
                <TableCell align="center">Unidad Origen</TableCell>
                <TableCell align="center">Unidad Destino</TableCell>
                <TableCell align="center">Factor</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversiones
                .filter(conv => {
                  const search = searchValue.toLowerCase();
                  return (
                    conv.unidad_origen_nombre.toLowerCase().includes(search) ||
                    conv.unidad_destino_nombre.toLowerCase().includes(search) ||
                    (conv.unidad_origen_abreviatura || '').toLowerCase().includes(search) ||
                    (conv.unidad_destino_abreviatura || '').toLowerCase().includes(search) ||
                    conv.id.toString().includes(search) ||
                    conv.factor.toString().includes(search)
                  );
                })
                .slice(rowsPerPage === -1 ? 0 : page * rowsPerPage, rowsPerPage === -1 ? undefined : (page + 1) * rowsPerPage)
                .map((conv) => (
                  <TableRow key={conv.id}>
                    <TableCell align="center" sx={{ pr: 8 }}>{conv.id}</TableCell>
                    <TableCell align="center">{conv.unidad_origen_nombre} ({conv.unidad_origen_abreviatura})</TableCell>
                    <TableCell align="center">{conv.unidad_destino_nombre} ({conv.unidad_destino_abreviatura})</TableCell>
                    <TableCell align="center">{conv.factor}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleOpen(conv)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(conv.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginacionControl
          page={page}
          rowsPerPage={rowsPerPage}
          count={conversiones.filter(conv => {
            const search = searchValue.toLowerCase();
            return (
              conv.unidad_origen_nombre.toLowerCase().includes(search) ||
              conv.unidad_destino_nombre.toLowerCase().includes(search) ||
              (conv.unidad_origen_abreviatura || '').toLowerCase().includes(search) ||
              (conv.unidad_destino_abreviatura || '').toLowerCase().includes(search) ||
              conv.id.toString().includes(search) ||
              conv.factor.toString().includes(search)
            );
          }).length}
          onPageChange={setPage}
        />
        <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold">
              {editId ? 'Editar Conversión' : 'Nueva Conversión'}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Autocomplete
              options={unidades}
              getOptionLabel={u => u.nombre + ' (' + u.abreviatura + ')'}
              value={unidades.find(u => u.id === unidadOrigen) || null}
              onChange={(e, value) => setUnidadOrigen(value ? value.id : '')}
              renderInput={params => <TextField {...params} label="Unidad Origen" margin="dense" />}
            />
            <Autocomplete
              options={unidades}
              getOptionLabel={u => u.nombre + ' (' + u.abreviatura + ')'}
              value={unidades.find(u => u.id === unidadDestino) || null}
              onChange={(e, value) => setUnidadDestino(value ? value.id : '')}
              renderInput={params => <TextField {...params} label="Unidad Destino" margin="dense" />}
            />
            <TextField
              label="Factor de Conversión"
              value={factor}
              onChange={e => setFactor(e.target.value)}
              fullWidth
              margin="dense"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">Cancelar</Button>
            <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>Guardar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Conversiones;
